import React, { useState, useEffect, useMemo } from 'react';
import { formatCurrency } from '../../utils/formatters';

const AddPaymentModal = ({ isOpen, onClose, onSave, sale }) => {
    const [amount, setAmount] = useState('');
    const [selectedInstallments, setSelectedInstallments] = useState(new Set());
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [useToday, setUseToday] = useState(true);

    const { totalPaid, remainingAmount, pendingInstallments } = useMemo(() => {
        if (!sale) return { totalPaid: 0, remainingAmount: 0, pendingInstallments: [] };
        
        const paid = sale.installments?.reduce((acc, inst) => acc + (inst.paidAmount || 0), 0) || 0;
        const remaining = (sale.totalAmount || 0) - paid;
        const pending = sale.installments?.filter(inst => inst.status === 'Pendente') || [];
        
        return { totalPaid: paid, remainingAmount: remaining, pendingInstallments: pending };
    }, [sale]);

    useEffect(() => {
        if (isOpen) {
            setAmount(remainingAmount > 0 ? remainingAmount.toFixed(2) : '');
            setSelectedInstallments(new Set());
            setUseToday(true);
            setPaymentDate(new Date().toISOString().split('T')[0]);
        }
    }, [isOpen, remainingAmount]);

    useEffect(() => {
        if (useToday) {
            setPaymentDate(new Date().toISOString().split('T')[0]);
        }
    }, [useToday]);
    
    useEffect(() => {
        if (selectedInstallments.size > 0) {
            const sumOfSelected = pendingInstallments
                .filter(inst => selectedInstallments.has(inst.installmentNumber))
                .reduce((acc, inst) => {
                    const amountOwed = inst.amount - (inst.paidAmount || 0);
                    return acc + amountOwed;
                }, 0);
            setAmount(sumOfSelected.toFixed(2));
        } else if (document.activeElement?.id !== 'paymentAmount') {
            // Só reseta o valor se o campo de valor não estiver focado,
            // permitindo a digitação de um valor customizado.
            setAmount('');
        }
    }, [selectedInstallments, pendingInstallments]);


    if (!isOpen || !sale) return null;
    
    const handleInstallmentSelection = (installmentNumber) => {
        setSelectedInstallments(prev => {
            const newSelection = new Set(prev);
            if (newSelection.has(installmentNumber)) {
                newSelection.delete(installmentNumber);
            } else {
                newSelection.add(installmentNumber);
            }
            return newSelection;
        });
    };

    const handleAmountChange = (e) => {
        setAmount(e.target.value);
        // Desmarca as parcelas se um valor customizado for digitado.
        if (selectedInstallments.size > 0) {
            setSelectedInstallments(new Set());
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const paymentAmount = parseFloat(amount);
        if (isNaN(paymentAmount) || paymentAmount <= 0) {
            alert('Por favor, insira um valor de pagamento válido.');
            return;
        }
        if (paymentAmount > remainingAmount + 0.01) { // Adiciona pequena margem para erros de arredondamento
            alert('O valor do pagamento não pode ser maior que o saldo devedor.');
            return;
        }
        
        const dateOfPayment = new Date(`${paymentDate}T00:00:00`);
        onSave(sale.id, paymentAmount, 'Pix', dateOfPayment);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-lg m-4 sm:m-8 max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Adicionar Pagamento</h2>
                <p className="text-gray-600 mb-4">Venda de: {(sale.items && sale.items.length > 0) ? sale.items[0].productName : sale.productName}</p>
                
                <div className="space-y-2 text-sm mb-6 bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between"><span className="font-medium text-gray-600">Valor Total da Venda:</span><span className="font-semibold">{formatCurrency(sale.totalAmount)}</span></div>
                    <div className="flex justify-between"><span className="font-medium text-gray-600">Valor Já Pago:</span><span className="font-semibold text-green-600">{formatCurrency(totalPaid)}</span></div>
                    <div className="flex justify-between border-t pt-2 mt-2"><span className="font-bold text-gray-800">Saldo Devedor:</span><span className="font-bold text-red-600">{formatCurrency(remainingAmount)}</span></div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {pendingInstallments.length > 0 && (
                        <div>
                            <label className="form-label">Selecionar Parcelas a Pagar</label>
                            <div className="space-y-2 border rounded-lg p-3 max-h-32 overflow-y-auto">
                                {pendingInstallments.map(inst => {
                                    const amountOwed = inst.amount - (inst.paidAmount || 0);
                                    return (
                                        <div key={inst.installmentNumber} className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <input 
                                                    type="checkbox" 
                                                    id={`inst-${inst.installmentNumber}`}
                                                    checked={selectedInstallments.has(inst.installmentNumber)}
                                                    onChange={() => handleInstallmentSelection(inst.installmentNumber)}
                                                    className="h-4 w-4 rounded border-gray-300 text-[#D7BFBD] focus:ring-[#D7BFBD]"
                                                />
                                                <label htmlFor={`inst-${inst.installmentNumber}`} className="ml-3 text-sm text-gray-700">
                                                    Parcela {inst.installmentNumber} ({new Date(inst.dueDate).toLocaleDateString('pt-BR')})
                                                </label>
                                            </div>
                                            <span className="font-medium">{formatCurrency(amountOwed)}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                    
                    <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                        <div className="flex-grow">
                            <label htmlFor="paymentDate" className="form-label">Data do Pagamento</label>
                            <input 
                                id="paymentDate" 
                                type="date" 
                                value={paymentDate} 
                                onChange={e => setPaymentDate(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg" 
                                readOnly={useToday}
                            />
                        </div>
                        <div className="flex items-center pb-2">
                            <input 
                                id="useToday" 
                                name="useToday" 
                                type="checkbox" 
                                checked={useToday} 
                                onChange={(e) => setUseToday(e.target.checked)} 
                                className="h-4 w-4 rounded border-gray-300 text-[#D7BFBD] focus:ring-[#D7BFBD]" 
                            />
                            <label htmlFor="useToday" className="ml-2 block text-sm text-gray-900">Hoje</label>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="paymentAmount" className="form-label">Valor do Pagamento (R$)</label>
                        <input 
                            id="paymentAmount" 
                            value={amount} 
                            onChange={handleAmountChange} 
                            type="number" 
                            step="0.01" 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg" 
                            required 
                        />
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="py-2 px-6 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 w-full sm:w-auto">Cancelar</button>
                        <button type="submit" className="custom-button py-2 px-6 font-semibold rounded-lg w-full sm:w-auto">Registrar Pagamento</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddPaymentModal;
